import { FieldShell } from '@today/ui-components'
import { Input, Textarea } from '@today/ui-primitives'
import type { ReactElement } from 'react'
import { MessagePreviewPane } from '../notification-template/message-preview-pane.view'
import { MessageVariableList } from '../notification-template/message-variable-list.view'
import type { MessagePickerOption, MessagePreviewView } from '../notification-template/notification-template-types'

/** The panel's built-in copy; override any entry for localisation. */
export interface OfferNotificationMessagePanelLabels {
	title: string
	picker: string
	editor: string
	editorHint: string
	subject: string
	variables: string
	variablesHint: string
	preview: string
	previewEmpty: string
}

/**
 * Props for {@link OfferNotificationMessagePanel}.
 *
 * @property options - The picker's options in display order (templates, then the custom choices).
 * @property selected - The selected option's value.
 * @property body - The editor text (a template's Liquid source, or the custom message).
 * @property subject - The selected EMAIL template's subject (read-only), or null.
 * @property preview - The rendered message, or null.
 * @property saveStatus - The save indicator text, or null.
 */
export interface OfferNotificationMessagePanelProps {
	options: readonly MessagePickerOption[]
	selected: string
	body: string
	maxLength: number
	subject: string | null
	preview: MessagePreviewView | null
	previewError: string | null
	variables: readonly string[]
	readOnly: boolean
	saveStatus: string | null
	onSelect: (value: string) => void
	onBodyChange: (body: string) => void
	labels?: Partial<OfferNotificationMessagePanelLabels>
}

const DEFAULT_LABELS: OfferNotificationMessagePanelLabels = {
	title: 'Customer notification',
	picker: 'Notification template',
	editor: 'Notification message',
	editorHint: 'Edit the text to turn it into a custom message for this offer.',
	subject: 'Subject',
	variables: 'Variables',
	variablesHint: 'Type a variable exactly as shown to insert it.',
	preview: 'Message preview',
	previewEmpty: 'Pick a template or write a message to see it rendered for this offer.',
}

const SELECT =
	'h-10 w-full rounded-ds-md border border-ds-border bg-ds-surface px-3 text-ds-base text-ds-text disabled:cursor-not-allowed disabled:opacity-50'

/**
 * Offer Wizard Step 3's notification-message panel (TO-140 offer-notification-message
 * REQ-106 / REQ-108): the template picker, the editable message, an EMAIL template's
 * read-only subject, the variable list and the rendered preview. Pure.
 */
export function OfferNotificationMessagePanel(props: OfferNotificationMessagePanelProps): ReactElement {
	const labels = { ...DEFAULT_LABELS, ...props.labels }
	return (
		<section aria-label={labels.title} className="flex flex-col gap-4">
			<h2 className="text-ds-lg font-semibold text-ds-text">{labels.title}</h2>
			<FieldShell label={labels.picker}>
				<select
					className={SELECT}
					value={props.selected}
					disabled={props.readOnly}
					onChange={(e) => props.onSelect(e.target.value)}
				>
					{props.options.map((option) => (
						<option key={option.value} value={option.value}>
							{option.label}
						</option>
					))}
				</select>
			</FieldShell>
			{props.subject !== null ? (
				<FieldShell label={labels.subject}>
					<Input value={props.subject} readOnly />
				</FieldShell>
			) : null}
			<FieldShell label={labels.editor} description={`${labels.editorHint} ${props.body.length} / ${props.maxLength}`}>
				<Textarea
					value={props.body}
					rows={6}
					maxLength={props.maxLength}
					readOnly={props.readOnly}
					onChange={(e) => props.onBodyChange(e.target.value)}
				/>
			</FieldShell>
			{props.saveStatus ? (
				<p className="text-ds-sm text-ds-text-muted" aria-live="polite">
					{props.saveStatus}
				</p>
			) : null}
			<MessageVariableList variables={props.variables} title={labels.variables} hint={labels.variablesHint} />
			<MessagePreviewPane
				title={labels.preview}
				preview={props.preview}
				error={props.previewError}
				emptyText={labels.previewEmpty}
				subjectLabel={labels.subject}
			/>
		</section>
	)
}
